import { useState, useRef, useContext } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  ColumnDef,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import Icon from '@mui/material/Icon';
import MDBox from 'src/components/md/MDBox';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import MDTypography from 'src/components/md/MDTypography';
import useFamousShipments, {
  FamousShipmentDTO,
} from 'src/api/temp/famous/useFamousShipments';
import useDeleteFamousShipment from 'src/api/temp/famous/useDeleteFamousShipment';
import { AuthContext } from 'src/context/auth.context';
import { useViewMode } from 'src/context/view-mode.context';
import { isAdmin } from 'src/utils/user';

interface FamousShipmentsTableProps {
  onSelectShipment: (shipment: FamousShipmentDTO) => void;
}

const FamousShipmentsTable: React.FC<FamousShipmentsTableProps> = ({
  onSelectShipment,
}) => {
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deletingShipmentId, setDeletingShipmentId] = useState<string | null>(
    null,
  );

  // Use a ref to track if we're in the middle of a pagination change
  const isPaginating = useRef(false);

  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const isAdminUser = isAdmin(currentUser, viewMode);

  const { data, isLoading, error, refetch } = useFamousShipments({
    page: pageIndex,
    pageSize: pageSize,
  });

  const deleteShipment = useDeleteFamousShipment(
    deletingShipmentId || '',
    () => {
      setDeletingShipmentId(null);
      refetch();
    },
  );

  const handleDeleteShipment = (e: React.MouseEvent, shipmentId: string) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this shipment?')) {
      setDeletingShipmentId(shipmentId);
      deleteShipment.mutate({});
    }
  };
  // Handle page change - fixed to update the pageIndex correctly
  const handlePageChange = (_: unknown, newPage: number) => {
    // MUI pagination is 0-based, but our API is 1-based
    isPaginating.current = true;
    setPageIndex(newPage + 1);
  };

  const columns: ColumnDef<FamousShipmentDTO>[] = [
    {
      accessorKey: 'index',
      header: '#',
      cell: (info) => {
        const rowIndex = info.row.index;
        const absoluteIndex = (pageIndex - 1) * pageSize + rowIndex + 1;
        return <MDTypography variant="caption">{absoluteIndex}</MDTypography>;
      },
      size: 50,
      minSize: 50,
      maxSize: 50,
    },
    {
      accessorKey: 'famousShipmentId',
      header: 'Famous Shipment ID',
      cell: (info) => {
        const shipmentId = info.getValue() as string;
        return (
          <MDTypography variant="caption" fontWeight="medium">
            {shipmentId || '-'}
          </MDTypography>
        );
      },
      minSize: 120,
      maxSize: 180,
    },
    {
      accessorKey: 'shipmentFetchStatus',
      header: 'Data Status',
      cell: (info) => {
        const row = info.row.original;
        const status = info.getValue() as string;
        const errorMessage =
          row.shipmentFetchError &&
          typeof row.shipmentFetchError === 'object' &&
          'message' in row.shipmentFetchError
            ? String(row.shipmentFetchError.message)
            : '';

        const getStatusColor = () => {
          switch (status) {
            case 'success':
              return 'success';
            case 'failed':
              return 'error';
            case 'fetching':
              return 'info';
            case 'pending':
            default:
              return 'default';
          }
        };

        const getStatusLabel = () => {
          switch (status) {
            case 'success':
              return 'Ready';
            case 'failed':
              return 'Failed';
            case 'fetching':
              return 'Loading...';
            case 'pending':
              return 'Pending';
            default:
              return status;
          }
        };

        const chip = (
          <Chip
            label={getStatusLabel()}
            color={getStatusColor()}
            size="small"
            variant="outlined"
          />
        );

        // Wrap with tooltip if status is failed and there's an error
        if (status === 'failed' && errorMessage) {
          return (
            <Tooltip
              title={errorMessage}
              slotProps={{
                tooltip: {
                  sx: {
                    maxWidth: '600px',
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    padding: 2,
                    fontSize: '12px',
                  },
                },
              }}
            >
              {chip}
            </Tooltip>
          );
        }

        return chip;
      },
      minSize: 100,
      maxSize: 130,
    },
    {
      id: 'edi856Status',
      header: 'EDI 856',
      cell: (info) => {
        const row = info.row.original;
        const { edi856SentAt, edi856Status } = row;

        if (edi856SentAt && edi856Status) {
          const sentDate = new Date(edi856SentAt);
          const dateStr = sentDate.toLocaleDateString();
          const timeStr = sentDate.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          });

          return (
            <MDBox>
              <Chip
                label={'Sent'}
                color="success"
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(
                    `https://ui.orderful.com/transactions/${edi856Status}`,
                    '_blank',
                  );
                }}
                sx={{ cursor: 'pointer' }}
              />
              <MDTypography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontSize: '0.7rem', mt: 0.5 }}
              >
                {dateStr} at {timeStr}
              </MDTypography>
            </MDBox>
          );
        }

        return (
          <Chip
            label="Not Sent"
            color="warning"
            size="small"
            variant="outlined"
          />
        );
      },
      minSize: 100,
      maxSize: 130,
    },
    {
      accessorKey: 'createdAt',
      header: 'Imported',
      cell: (info) => {
        const value = info.getValue() as Date | string;

        const date = typeof value === 'string' ? new Date(value) : value;
        const dateStr = date.toLocaleDateString();
        const timeStr = date.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        });

        return (
          <MDBox>
            <MDTypography variant="caption" sx={{ display: 'block' }}>
              {dateStr}
            </MDTypography>
            <MDTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', fontSize: '0.7rem' }}
            >
              {timeStr}
            </MDTypography>
          </MDBox>
        );
      },
      size: 120,
      minSize: 120,
      maxSize: 120,
    },
    // Delete column - only for admins
    ...(isAdminUser
      ? [
          {
            id: 'delete',
            header: '',
            cell: (info) => {
              const row = info.row.original;
              const isDeleting = deletingShipmentId === row.id;

              return (
                <Tooltip title="Delete shipment">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => handleDeleteShipment(e, row.id)}
                    disabled={isDeleting || deleteShipment.isLoading}
                    sx={{
                      opacity: 0.6,
                      '&:hover': {
                        opacity: 1,
                      },
                    }}
                  >
                    <Icon fontSize="small">
                      {isDeleting ? 'hourglass_empty' : 'delete'}
                    </Icon>
                  </IconButton>
                </Tooltip>
              );
            },
            size: 50,
            minSize: 50,
            maxSize: 50,
          } as ColumnDef<FamousShipmentDTO>,
        ]
      : []),
  ];

  const table = useReactTable({
    data: data?.edges || [],
    columns,
    pageCount: data?.pageInfo?.totalPages || -1,
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: pageIndex - 1,
        pageSize,
      },
    },
    onPaginationChange: (updater) => {
      const next =
        typeof updater === 'function'
          ? updater({ pageIndex: pageIndex - 1, pageSize })
          : updater;
      setPageIndex(next.pageIndex + 1);
      setPageSize(next.pageSize);
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (isLoading && !isPaginating.current) {
    return (
      <MDBox display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </MDBox>
    );
  }

  if (error) {
    return (
      <MDBox display="flex" justifyContent="center" p={3}>
        <ErrorMessage error={error} />
      </MDBox>
    );
  }

  return (
    <MDBox>
      <TableContainer sx={{ boxShadow: 'none' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              {table.getHeaderGroups().map((headerGroup) =>
                headerGroup.headers.map((header, index) => {
                  // Define percentage widths for each column
                  const columnWidths = isAdminUser
                    ? [
                        '5%', // #
                        '18%', // Famous Shipment ID
                        '12%', // Data Status
                        '13%', // EDI 856
                        '13%', // Imported
                        '5%', // Delete (admin only)
                      ]
                    : [
                        '5%', // #
                        '20%', // Famous Shipment ID
                        '12%', // Data Status
                        '15%', // EDI 856
                        '15%', // Imported
                      ];

                  return (
                    <TableCell
                      key={header.id}
                      sx={{
                        position: 'relative',
                        textAlign: 'left',
                        opacity: 0.7,
                        background: 'transparent',
                        color: 'rgb(123, 128, 154)',
                        boxShadow: 'none',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                        width: columnWidths[index],
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : header.column.columnDef.header instanceof Function
                          ? header.column.columnDef.header(header.getContext())
                          : header.column.columnDef.header}
                    </TableCell>
                  );
                }),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
                onClick={() => onSelectShipment(row.original)}
              >
                {row.getVisibleCells().map((cell, index) => {
                  const columnWidths = isAdminUser
                    ? [
                        '5%', // #
                        '18%', // Famous Shipment ID
                        '12%', // Data Status
                        '13%', // EDI 856
                        '13%', // Imported
                        '5%', // Delete (admin only)
                      ]
                    : [
                        '5%', // #
                        '20%', // Famous Shipment ID
                        '12%', // Data Status
                        '15%', // EDI 856
                        '15%', // Imported
                      ];

                  return (
                    <TableCell
                      key={cell.id}
                      sx={{
                        color: '#7b809a',
                        fontSize: '0.875rem',
                        fontWeight: '400',
                        width: columnWidths[index],
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {cell.column.columnDef.cell instanceof Function
                        ? cell.column.columnDef.cell(cell.getContext())
                        : String(cell.getValue() ?? '')}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={data?.pageInfo?.totalCount || 0}
        page={pageIndex - 1}
        onPageChange={handlePageChange}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          setPageSize(Number(e.target.value));
          setPageIndex(1);
        }}
        nextIconButtonProps={{
          disabled: !data?.pageInfo?.hasNextPage,
        }}
        backIconButtonProps={{
          disabled: !data?.pageInfo?.hasPreviousPage,
        }}
      />
    </MDBox>
  );
};

export default FamousShipmentsTable;
